import {useState, useCallback, useEffect} from 'react'
import {ModuleRegistry, AllCommunityModule} from 'ag-grid-community'
import {Button} from '@origamirisk/origami-storybook'
import {CircularProgress} from '@mui/material'
import {useAccount} from '../context/AccountContext'
import {BulkRatingGrid, CreateBulkRatingModal, BulkRatingRequest, BulkRatingCreatedResponse} from '../components/BulkRating'
import {useBulkRatingProgress, ProgressUpdate} from '../hooks/useBulkRatingProgress'
import './BulkRating.css'

ModuleRegistry.registerModules([AllCommunityModule])

interface BulkRatingApiResponse {
    requestId: string
    policySetId: number
    policySetName: string
    totalCount: number
    status: string
    requestPayload: {
        states?: string[],
        proposalIds?: number[],
    }
    createdAt: string
    completedAt?: string
}

async function fetchBulkRatingRequests(accountName: string): Promise<BulkRatingRequest[]> {
    const response = await fetch('/api/bulk-rating/requests', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({accountName})
    })
    if (!response.ok) {
        throw new Error('Failed to fetch bulk rating requests')
    }
    const data: BulkRatingApiResponse[] = await response.json()
    return data.map(item => ({
        requestId: item.requestId,
        status: item.status,
        policySetId: item.policySetId,
        policySetName: item.policySetName,
        requestPayload: {
            states: item.requestPayload?.states,
            proposalIds: item.requestPayload?.proposalIds
        },
        totalCount: item.totalCount,
        progress: item.status === 'DONE' ? 100 : 0,
        createdAt: item.createdAt,
        completedAt: item.completedAt
    }))
}

async function deleteBulkRatingRequest(requestId: string): Promise<void> {
    const response = await fetch('/api/bulk-rating/delete', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({requestId})
    })
    if (!response.ok) {
        throw new Error('Failed to delete bulk rating request')
    }
}

function BulkRating() {
    const {selectedAccount} = useAccount()
    const [bulkRequests, setBulkRequests] = useState<BulkRatingRequest[]>([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [activeRequest, setActiveRequest] = useState<{ requestId: string, totalCount: number } | null>(null)

    const refreshRequests = useCallback(() => {
        if (!selectedAccount) return Promise.resolve()
        return fetchBulkRatingRequests(selectedAccount)
            .then(data => setBulkRequests(data))
            .catch(err => console.error('Failed to load bulk requests:', err))
    }, [selectedAccount])

    useEffect(() => {
        if (!selectedAccount) return

        setIsLoading(true)
        fetchBulkRatingRequests(selectedAccount)
            .then(data => {
                setBulkRequests(data)
                // Find any in-progress request and start listening
                const inProgress = data.find(r => r.status !== 'DONE' && r.status !== 'completed' && r.progress < 100)
                if (inProgress) {
                    setActiveRequest({requestId: inProgress.requestId, totalCount: inProgress.totalCount})
                }
            })
            .catch(err => console.error('Failed to load bulk requests:', err))
            .finally(() => setIsLoading(false))
    }, [selectedAccount])

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
    }

    const handleDelete = useCallback((requestId: string) => {
        deleteBulkRatingRequest(requestId)
            .then(() => {
                setBulkRequests(prev => prev.filter(r => r.requestId !== requestId))
                if (activeRequest?.requestId === requestId) {
                    setActiveRequest(null)
                }
            })
            .catch(err => console.error('Failed to delete request:', err))
    }, [activeRequest])

    const handleProgress = useCallback((update: ProgressUpdate) => {
        if (update.type === 'progress' && update.requestId) {
            setBulkRequests(prev => prev.map(r =>
                r.requestId === update.requestId
                    ? {...r, progress: update.progress ?? r.progress}
                    : r
            ))
            if (update.progress === 100) {
                setActiveRequest(null)
                // Refresh to get final data
                refreshRequests()
            }
        } else if (update.type === 'complete' && update.requestId) {
            // Set to 100% immediately on complete
            setBulkRequests(prev => prev.map(r =>
                r.requestId === update.requestId
                    ? {...r, progress: 100}
                    : r
            ))
            setActiveRequest(null)
            refreshRequests()
        }
    }, [refreshRequests])

    const handleCreated = useCallback((response: BulkRatingCreatedResponse) => {
        if (!selectedAccount) return

        fetchBulkRatingRequests(selectedAccount)
            .then(data => {
                setBulkRequests(data)
                setActiveRequest({requestId: response.requestId, totalCount: response.totalCount})
            })
            .catch(err => console.error('Failed to reload bulk requests:', err))
    }, [selectedAccount])

    useBulkRatingProgress({
        requestId: activeRequest?.requestId ?? '',
        totalCount: activeRequest?.totalCount ?? 0,
        onProgress: handleProgress,
        enabled: !!activeRequest
    })

    return (
        <div className="bulk-rating">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleOpenModal}
                >
                    Create Bulk Rating
                </Button>
            </div>

            {isLoading ? (
                <div style={{display: 'flex', justifyContent: 'center', padding: '40px'}}>
                    <CircularProgress/>
                </div>
            ) : (
                <BulkRatingGrid bulkRequests={bulkRequests} onDelete={handleDelete}/>
            )}

            {isModalOpen && (
                <CreateBulkRatingModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    onCreated={handleCreated}
                    accountName={selectedAccount || ''}
                />
            )}
        </div>
    )
}

export default BulkRating
